/**
 * One company: what it is to the organisation, whatever roles it holds.
 *
 * A company can hold several roles at once. As a data buyer ("Buys from us")
 * it has a buyer record that routing sells to; as a data seller ("Sells to
 * us") a supplier record that leads arrive from; as a customer, deals. The
 * role tabs show and edit those records through the same endpoints and
 * permissions the old Buyers and Suppliers pages used, and routing keeps
 * reading them by their own ids. The CRM sections (details, owner,
 * properties, timeline, deals) are as before.
 *
 * `?tab=buys` or `?tab=sells` opens a role's tab; the old `/buyers/:id`
 * addresses land there.
 *
 * Merging is in the menu. It asks which company to fold in, says exactly
 * what will move, and shows the server's reason if the two cannot merge
 * (both are data buyers, or both are data sellers).
 */
import { useEffect, useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Button, Card, Group, Loader, Menu, Modal, Select, SimpleGrid, Stack, Table, Tabs,
  Text, TextInput, Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft, IconBuildingStore, IconAddressBook, IconDots, IconGitMerge, IconPlus, IconTrash,
} from '@tabler/icons-react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useAddCompanyRole, useCompanies, useCompany, useDeleteCompany, useMergeCompany, useUpdateCompany,
  type CompanyDetail, type CompanyRoleKey,
} from '../../api/crm';
import { FONTS, TOKENS } from '../../theme';
import { MonoLabel, PageHeader } from '../../ui';
import { useVocab } from '../../vocab';
import {
  formatDay, formatPence, OwnerSelect, RoleChips, roleWord, statusColour, STATUS_LABEL, useCrmPermissions,
} from './crmShared';
import { DealDrawer } from './DealDrawer';
import { PropertiesPanel } from '../properties/PropertyFields';
import { NewDealModal } from './DealForms';
import { TimelinePanel } from './Timeline';
import { BuysFromUs, RoleSummary, SellsToUs } from './CompanyRoles';

const TABS = ['overview', 'buys', 'sells', 'timeline', 'deals'] as const;
type Tab = (typeof TABS)[number];

export function CompanyPage() {
  const { id = '' } = useParams();
  const v = useVocab();
  const navigate = useNavigate();
  const perms = useCrmPermissions();
  const [params, setParams] = useSearchParams();
  const { data: company, isPending, error } = useCompany(id);
  const remove = useDeleteCompany();
  const addRole = useAddCompanyRole();
  const [merging, setMerging] = useState(false);
  const [newDeal, setNewDeal] = useState(false);
  const [openDeal, setOpenDeal] = useState<string | null>(null);

  const asked = params.get('tab');
  const tab: Tab = TABS.includes(asked as Tab) ? (asked as Tab) : 'overview';
  const setTab = (t: string | null) => {
    const next = new URLSearchParams(params);
    if (!t || t === 'overview') next.delete('tab');
    else next.set('tab', t);
    setParams(next, { replace: true });
  };

  if (company?.merged_into) return <Navigate to={`/companies/${company.merged_into}`} replace />;

  // Which sections this person sees: the role tabs follow the old Buyers and
  // Suppliers pages, the CRM ones the Companies and Deals pages.
  const crm = perms.seesCompanies;
  const showBuys = !!company?.partner && perms.seesBuyers;
  const showSells = !!company?.source && perms.seesSuppliers;
  const showDeals = crm && perms.seesDeals;
  const showTimeline = crm && perms.seesTimeline;
  const shown: Record<Tab, boolean> = {
    overview: true, buys: showBuys, sells: showSells, timeline: showTimeline, deals: showDeals,
  };
  const current: Tab = shown[tab] ? tab : 'overview';
  const archived = company
    ? { data_buyer: company.partner?.active === false, data_seller: company.source?.active === false }
    : undefined;

  async function del() {
    if (!company) return;
    if (!window.confirm(`Delete ${company.name}? Its ${v.many('deal')} stay, without a ${v.one('company')}.`)) return;
    try {
      await remove.mutateAsync(company.id);
      navigate('/companies');
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not deleted', message: (e as Error).message });
    }
  }

  async function add(role: CompanyRoleKey) {
    if (!company) return;
    try {
      await addRole.mutateAsync({ id: company.id, role });
      notifications.show({
        color: 'teal',
        title: `${roleWord(v, role)} role added`,
        message: `${company.name} now has ${role === 'data_buyer' ? v.a('buyer') : v.a('supplier')} record. Set it up on its tab.`,
      });
      setTab(role === 'data_buyer' ? 'buys' : 'sells');
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not added', message: (e as Error).message });
    }
  }

  const canAddBuyer = !!company && !company.partner && perms.editBuyers && perms.seesBuyers;
  const canAddSeller = !!company && !company.source && perms.editSuppliers && perms.seesSuppliers;
  const hasMenu = perms.editCompanies || canAddBuyer || canAddSeller;

  return (
    <Stack gap="md">
      <Anchor component={Link} to={crm ? '/companies' : company?.partner ? '/buyers' : '/suppliers'} size="sm" c="dimmed">
        <Group gap={4}><IconArrowLeft size={14} />{v.Many('company')}</Group>
      </Anchor>
      {error && <Alert color="red" variant="light">{error.message}</Alert>}
      {isPending && <Group justify="center" p="xl"><Loader /></Group>}
      {company && (
        <>
          <PageHeader
            title={company.name}
            status={<RoleChips roles={company.role_keys} archived={archived} />}
            description={company.domain || undefined}
            actions={(
              <>
                {showDeals && perms.editDeals && (
                  <Button leftSection={<IconPlus size={16} />} onClick={() => setNewDeal(true)}>
                    {`New ${v.one('deal')}`}
                  </Button>
                )}
                {hasMenu && (
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <ActionIcon variant="default" size="lg" aria-label={`${v.One('company')} actions`}>
                        <IconDots size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {canAddBuyer && (
                        <Menu.Item leftSection={<IconBuildingStore size={14} />} onClick={() => add('data_buyer')}>
                          {`Add ${v.p.dataBuyerLower} role`}
                        </Menu.Item>
                      )}
                      {canAddSeller && (
                        <Menu.Item leftSection={<IconAddressBook size={14} />} onClick={() => add('data_seller')}>
                          {`Add ${v.p.dataSellerLower} role`}
                        </Menu.Item>
                      )}
                      {perms.editCompanies && crm && (
                        <Menu.Item leftSection={<IconGitMerge size={14} />} onClick={() => setMerging(true)}>
                          {`Merge another ${v.one('company')} into this one`}
                        </Menu.Item>
                      )}
                      {perms.editCompanies && crm && (
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          disabled={!!company.partner || !!company.source}
                          onClick={del}
                        >
                          {`Delete ${v.one('company')}`}
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                )}
              </>
            )}
          />

          <Tabs value={current} onChange={setTab} keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              {showBuys && <Tabs.Tab value="buys">Buys from us</Tabs.Tab>}
              {showSells && <Tabs.Tab value="sells">Sells to us</Tabs.Tab>}
              {showTimeline && <Tabs.Tab value="timeline">Timeline</Tabs.Tab>}
              {showDeals && <Tabs.Tab value="deals">{`${v.Many('deal')} (${company.deal_totals.open_count})`}</Tabs.Tab>}
            </Tabs.List>

            <Tabs.Panel value="overview" pt="md">
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" style={{ alignItems: 'start' }}>
                <Stack gap="md">
                  <Overview company={company} canEdit={perms.editCompanies && crm} />
                  {crm && perms.recordProperties && <CompanyProperties company={company} canEdit={perms.editCompanies} />}
                </Stack>
                <Stack gap="md">
                  <RoleSummary company={company} perms={perms} onOpen={setTab} />
                  {showDeals && (
                    <Card p="md" data-testid="deal-summary">
                      <Stack gap="sm">
                        <MonoLabel>{v.Many('deal')}</MonoLabel>
                        <Group gap="xl" wrap="wrap">
                          <Figure label={`Open ${v.many('deal')}`} value={`${company.deal_totals.open_count}`} />
                          <Figure label="Open value" value={formatPence(company.deal_totals.open_amount_pence)} />
                          <Figure label="Won" value={formatPence(company.deal_totals.won_amount_pence)} />
                        </Group>
                        {company.open_deals.slice(0, 3).map((d) => (
                          <Group key={d.id} justify="space-between" wrap="nowrap">
                            <Anchor component="button" size="sm" onClick={() => setOpenDeal(d.id)}>{d.name}</Anchor>
                            <Text size="sm" c="dimmed">{`${d.stage_name} · ${formatPence(d.amount_pence)}`}</Text>
                          </Group>
                        ))}
                      </Stack>
                    </Card>
                  )}
                  {!company.partner && !company.source && !showDeals && (
                    <Text size="sm" c="dimmed">{`${company.name} holds no roles yet.`}</Text>
                  )}
                </Stack>
              </SimpleGrid>
            </Tabs.Panel>

            {showBuys && (
              <Tabs.Panel value="buys" pt="md">
                <BuysFromUs company={company} perms={perms} />
              </Tabs.Panel>
            )}
            {showSells && (
              <Tabs.Panel value="sells" pt="md">
                <SellsToUs company={company} perms={perms} />
              </Tabs.Panel>
            )}

            {showTimeline && (
              <Tabs.Panel value="timeline" pt="md">
                <Card p="md" maw={760}>
                  <TimelinePanel objectType="company" objectId={company.id} />
                </Card>
              </Tabs.Panel>
            )}

            {showDeals && (
              <Tabs.Panel value="deals" pt="md">
                <Card p={0}>
                  <Table.ScrollContainer minWidth={560}>
                    <Table verticalSpacing={8} highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Name</Table.Th>
                          <Table.Th>{v.One('stage')}</Table.Th>
                          <Table.Th ta="right">Amount</Table.Th>
                          <Table.Th>Close date</Table.Th>
                          <Table.Th>Owner</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {company.open_deals.length === 0 && (
                          <Table.Tr><Table.Td colSpan={5}>
                            <Text ta="center" c="dimmed" py="lg">{`No open ${v.many('deal')}.`}</Text>
                          </Table.Td></Table.Tr>
                        )}
                        {company.open_deals.map((d) => (
                          <Table.Tr key={d.id} onClick={() => setOpenDeal(d.id)} style={{ cursor: 'pointer' }}>
                            <Table.Td><Text size="sm" fw={500}>{d.name}</Text></Table.Td>
                            <Table.Td>
                              <Group gap={6}>
                                <Text size="sm">{d.stage_name}</Text>
                                {d.status !== 'open' && (
                                  <Badge size="xs" variant="light" color={statusColour(d.status)}>{STATUS_LABEL[d.status]}</Badge>
                                )}
                              </Group>
                            </Table.Td>
                            <Table.Td ta="right">
                              <span style={{ fontFamily: FONTS.mono, fontSize: 13 }}>{formatPence(d.amount_pence)}</span>
                            </Table.Td>
                            <Table.Td><Text size="sm">{formatDay(d.close_date)}</Text></Table.Td>
                            <Table.Td><Text size="sm">{d.owner_name ?? ''}</Text></Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </Card>
                <Anchor component={Link} to={`/deals?view=table&q=${encodeURIComponent(company.name)}`} size="sm" mt="sm" display="inline-block">
                  {`All ${v.many('deal')} for ${company.name}, won and lost included`}
                </Anchor>
              </Tabs.Panel>
            )}
          </Tabs>

          {crm && (
            <MergeCompanyModal
              opened={merging}
              company={company}
              onClose={() => setMerging(false)}
            />
          )}
          {showDeals && (
            <>
              <NewDealModal
                opened={newDeal}
                onClose={() => setNewDeal(false)}
                prefill={{ companyId: company.id, companyName: company.name }}
                onCreated={(dealId) => { setNewDeal(false); setOpenDeal(dealId); }}
              />
              <DealDrawer id={openDeal} onClose={() => setOpenDeal(null)} />
            </>
          )}
        </>
      )}
    </Stack>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <MonoLabel>{label}</MonoLabel>
      <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: TOKENS.text, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
    </div>
  );
}

/** The company's properties: saved on their own, merged by the server. */
function CompanyProperties({ company, canEdit }: { company: CompanyDetail; canEdit: boolean }) {
  const update = useUpdateCompany();
  return (
    <PropertiesPanel
      object="company"
      values={(company.properties ?? {}) as Record<string, unknown>}
      canEdit={canEdit}
      saving={update.isPending}
      onSave={(patch) => update.mutateAsync({ id: company.id, body: { properties: patch } })}
    />
  );
}

function Overview({ company, canEdit }: { company: CompanyDetail; canEdit: boolean }) {
  const update = useUpdateCompany();
  const [draft, setDraft] = useState({ name: '', domain: '', phone: '', address: '', owner: null as string | null });
  const [error, setError] = useState('');
  useEffect(() => {
    setDraft({
      name: company.name,
      domain: company.domain,
      phone: company.phone,
      address: company.address,
      owner: company.owner_user_id ?? null,
    });
  }, [company]);
  const dirty = draft.name.trim() !== company.name || draft.domain !== company.domain
    || draft.phone !== company.phone || draft.address !== company.address
    || draft.owner !== (company.owner_user_id ?? null);

  async function save() {
    if (!draft.name.trim()) {
      setError('A name is required.');
      return;
    }
    setError('');
    try {
      await update.mutateAsync({
        id: company.id,
        body: {
          name: draft.name.trim(),
          domain: draft.domain,
          phone: draft.phone,
          address: draft.address,
          owner_user_id: draft.owner ?? '',
        },
      });
      notifications.show({ color: 'teal', title: 'Saved', message: draft.name.trim() });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Card p="md" maw={640}>
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <TextInput label="Name" disabled={!canEdit} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })} />
        <Group grow wrap="wrap" align="flex-start">
          <TextInput label="Domain" disabled={!canEdit} value={draft.domain} onChange={(e) => setDraft({ ...draft, domain: e.currentTarget.value })} miw={200} />
          <TextInput label="Phone" disabled={!canEdit} value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.currentTarget.value })} miw={160} />
        </Group>
        <Textarea label="Address" disabled={!canEdit} autosize minRows={2} value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.currentTarget.value })} />
        <OwnerSelect label="Owner" disabled={!canEdit} value={draft.owner} onChange={(o) => setDraft({ ...draft, owner: o })} />
        {canEdit && (
          <Group justify="flex-end">
            <Button onClick={save} disabled={!dirty} loading={update.isPending}>Save</Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Fold another company into this one, after saying what moves. The server
 * refuses two partners or two sources, and its reason is shown as is.
 */
export function MergeCompanyModal({
  opened, company, onClose,
}: { opened: boolean; company: CompanyDetail; onClose: () => void }) {
  const v = useVocab();
  const others = useCompanies({ page_size: 500 }, opened).data?.companies.filter((c) => c.id !== company.id) ?? [];
  const merge = useMergeCompany();
  const [otherId, setOtherId] = useState<string | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (opened) {
      setOtherId(null);
      setError('');
    }
  }, [opened]);
  const other = others.find((c) => c.id === otherId);

  async function confirm() {
    if (!otherId) return;
    setError('');
    try {
      const r = await merge.mutateAsync({ id: company.id, otherId });
      notifications.show({
        color: 'teal',
        title: 'Merged',
        message: `${other?.name ?? 'The other'} is now part of ${r.company.name}.`,
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const moves: string[] = [];
  if (other) {
    if (other.role_keys.includes('data_buyer')) moves.push(`its ${v.one('buyer')} record, with the same id, so routing is unchanged`);
    if (other.role_keys.includes('data_seller')) moves.push(`its ${v.one('supplier')} record, with the same id`);
    moves.push(`its ${v.many('deal')} (${other.open_deals} open)`);
  }

  return (
    <Modal opened={opened} onClose={onClose} title={`Merge into ${company.name}`} size="md">
      <Stack>
        <Select
          label={`${v.One('company')} to merge in`}
          placeholder="Choose by name"
          searchable
          data={others.map((c) => ({ value: c.id, label: c.name }))}
          value={otherId}
          onChange={setOtherId}
          nothingFoundMessage={`No ${v.one('company')} by that name`}
        />
        {other && (
          <Alert variant="light" color="blue" title="What happens" data-testid="merge-summary">
            <Text size="sm">{`${other.name} becomes part of ${company.name}. Moving across:`}</Text>
            <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
              {moves.map((m) => <li key={m}><Text size="sm">{m}</Text></li>)}
            </ul>
            <Text size="sm">
              {`Fields ${company.name} leaves empty take ${other.name}'s values. ${other.name} stops appearing in lists, and links to it open ${company.name}.`}
            </Text>
          </Alert>
        )}
        {error && <Alert color="red" variant="light" title="Not merged">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button disabled={!otherId} loading={merge.isPending} onClick={confirm}>Merge</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
